import { clamp01, smoothstep } from "@bfly/engine/math";
import { FACE_EDGE, type HalftoneData } from "@bfly/engine";

/** How many columns the face's outline is split into across its span (`outlineHalfWidth`).
    That's about 5px each on a desktop. */
export const OUTLINE_COLUMNS = 160;

// The radius of the arch the edge falls away in, as a fraction of the portrait's side (about
// 24px on a desktop). A notch a column or two wide fills in to within a few pixels, and a
// sudden jump becomes a curve.
const ARCH = 0.05;

// Half the turned box's width (and height), as a fraction of the side.
const boxHalf = (angle: number): number =>
  0.5 * (Math.abs(Math.cos(angle)) + Math.abs(Math.sin(angle)));

// How far sideways an arch reaches before it has dropped the box's whole height, as a
// fraction of the side.
const archReach = (half: number): number => Math.sqrt(2 * ARCH * 2 * half);

/** Half the outline's span across the screen, as a fraction of the portrait's side: the
    turned box plus an arch's reach on each side. That way the arch gets all the way up to
    the box's top before the outline ends. Ending it any lower left a straight edge. */
export function outlineHalfWidth(angle: number): number {
  const half = boxHalf(angle);
  return half + archReach(half);
}

/** The face's bottom edge on screen, column by column. Column `i` runs from
    `left + i * step` to the next one, in viewport pixels.

    - `bottoms[i]`: the edge's y right now, in viewport pixels.
    - `restBottoms[i]`: its y on the page while the portrait still scrolls with it, before
      it locks, in page pixels. */
export interface PortraitOutline {
  left: number;
  step: number;
  bottoms: Float32Array;
  restBottoms: Float32Array;
}

/** The face's bottom edge on screen, for each of `OUTLINE_COLUMNS` columns across the outline's
    span (`outlineHalfWidth`, centered on the portrait). Each value is how far below the
    portrait's center the edge is, as a fraction of the portrait's side.

    The portrait turns by `angle` about its center (radians, like CSS `rotate()`: negative is
    counterclockwise), so each dot goes where the turn puts it. Dots that the tapered edge
    drops (`FACE_EDGE`, minus its noise) don't count, and a column with no dots sits at the
    box's top.

    Then each column takes the lowest edge near it, minus a drop that grows with the square
    of the distance (`ARCH`). Narrow notches fill in, so no text slips into them, and where the
    edge jumps (the sides of the face) it falls away in an arch instead of a flat shelf with
    walls. */
export function faceOutline(
  data: Pick<HalftoneData, "n" | "w" | "h" | "x" | "y" | "r">,
  angle: number,
): Float32Array {
  const { edgeMargin, span, start, end, power, minKeep } = FACE_EDGE;
  // The average depth of the other edges. The noise varies it from 0.35 to 1 times the margin.
  const margin = edgeMargin * 0.675;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const half = boxHalf(angle);
  const halfWidth = outlineHalfWidth(angle);
  const lowest = new Float32Array(OUTLINE_COLUMNS).fill(-half);
  for (let i = 0; i < data.n; i++) {
    const x = data.x[i] ?? 0;
    const y = data.y[i] ?? 0;
    const along = clamp01(1 - x / span);
    const taper = start + (end - start) * along ** power;
    const keep = Math.min(
      smoothstep(0, 1, (data.h - y) / taper),
      smoothstep(0, 1, x / margin),
      smoothstep(0, 1, y / margin),
      smoothstep(0, 1, (data.w - x) / margin),
    );
    if (keep < minKeep) continue;
    // Relative to the portrait's center, as fractions of its side, then turned.
    const u = x / data.w - 0.5;
    const v = y / data.h - 0.5;
    const across = u * cos - v * sin;
    const down = u * sin + v * cos + ((data.r[i] ?? 0) / data.w) * keep;
    const column = Math.min(
      OUTLINE_COLUMNS - 1,
      Math.max(0, Math.floor(((across + halfWidth) / (2 * halfWidth)) * OUTLINE_COLUMNS)),
    );
    lowest[column] = Math.max(lowest[column] ?? -half, down);
  }
  // A column's width, and how many columns away a neighbor can still matter. Past that, the
  // drop is more than the box's whole height.
  const width = (2 * halfWidth) / OUTLINE_COLUMNS;
  const reach = Math.ceil(archReach(half) / width);
  const bottoms = new Float32Array(OUTLINE_COLUMNS);
  for (let c = 0; c < OUTLINE_COLUMNS; c++) {
    let bottom = -half;
    for (let k = Math.max(0, c - reach); k <= Math.min(OUTLINE_COLUMNS - 1, c + reach); k++) {
      const away = (k - c) * width;
      bottom = Math.max(bottom, (lowest[k] ?? -half) - (away * away) / (2 * ARCH));
    }
    bottoms[c] = bottom;
  }
  return bottoms;
}

/** The portrait's turn, in radians, from its computed transform (a rotation only). */
export function portraitTurn(portrait: HTMLElement): number {
  const transform = getComputedStyle(portrait).transform;
  const matrix = new DOMMatrix(transform === "none" ? undefined : transform);
  return Math.atan2(matrix.b, matrix.a);
}

/** Reports the face's outline to `onOutline` (`PortraitOutline` has the units): right away,
    after each scroll, and after each resize, which also measures where it rests before the
    portrait locks. It starts as soon as the data loads, so the text fade follows the static
    print too. */
export function trackOutline(
  portrait: HTMLElement,
  data: Pick<HalftoneData, "n" | "w" | "h" | "x" | "y" | "r">,
  onOutline: (outline: PortraitOutline) => void,
  signal: AbortSignal,
): void {
  const turn = portraitTurn(portrait);
  const outline = faceOutline(data, turn);
  const halfWidth = outlineHalfWidth(turn);
  const bottoms = new Float32Array(outline.length);
  const restBottoms = new Float32Array(outline.length);
  // The turn doesn't move the box's center, and the layout width is the side.
  const place = (rect: DOMRect, i: number): number =>
    rect.top + rect.height / 2 + (outline[i] ?? 0) * portrait.offsetWidth;
  // Where the outline sits on the page before the portrait locks. Sticky positioning is
  // switched off for one measurement, on load and resize only.
  const measureRest = (): void => {
    portrait.style.position = "static";
    const rect = portrait.getBoundingClientRect();
    portrait.style.removeProperty("position");
    for (let i = 0; i < outline.length; i++) restBottoms[i] = place(rect, i) + window.scrollY;
  };
  const report = (): void => {
    const rect = portrait.getBoundingClientRect();
    for (let i = 0; i < outline.length; i++) bottoms[i] = place(rect, i);
    const side = portrait.offsetWidth;
    onOutline({
      left: rect.left + rect.width / 2 - halfWidth * side,
      step: (2 * halfWidth * side) / outline.length,
      bottoms,
      restBottoms,
    });
  };
  measureRest();
  report();
  window.addEventListener("scroll", report, { passive: true, signal });
  window.addEventListener(
    "resize",
    () => {
      measureRest();
      report();
    },
    { signal },
  );
}
